import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Avatar, Chip, Skeleton } from "../../components/ui";
import { useThemeColors } from "../../hooks/useThemeColors";
import {
  ReviewItem,
  JobCardQueue,
  ReportItem,
  TaskItem,
  getItem,
  getReport,
  getTask,
} from "./jobCardApi";
import { CHIP_STATUS, formatDate, money, statusLabel } from "./jobCardShared";
import { ApprovalPanel, ReportPanel, TaskPanel } from "./ActionPanels";
import CommentThread from "./CommentThread";

type Loaded =
  | { queue: "proposal" | "assignment"; data: ReviewItem }
  | { queue: "task"; data: TaskItem }
  | { queue: "report"; data: ReportItem };

interface Props {
  target: { queue: JobCardQueue; id: number } | null;
  onClose: () => void;
  onChanged: () => void;
}

const DetailModal = ({ target, onClose, onChanged }: Props) => {
  const colors = useThemeColors();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoaded(null);
    setError(null);
    if (!target) return;
    let active = true;
    const { queue, id } = target;
    const request =
      queue === "task"
        ? getTask(id).then((data): Loaded => ({ queue, data }))
        : queue === "report"
          ? getReport(id).then((data): Loaded => ({ queue, data }))
          : getItem(queue, id).then((data): Loaded => ({ queue, data }));
    request
      .then((l) => active && setLoaded(l))
      .catch(() => active && setError("Could not load this item."));
    return () => {
      active = false;
    };
  }, [target]);

  const done = () => {
    onChanged();
    onClose();
  };

  const field = (label: string, value: React.ReactNode) => (
    <Box>
      <Typography sx={{ fontSize: "0.72rem", color: colors.primary[300], mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: colors.primary[100] }}>
        {value}
      </Typography>
    </Box>
  );

  const grid = (children: React.ReactNode) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: 2,
        p: 2,
        borderRadius: "12px",
        backgroundColor: colors.primary[800],
      }}
    >
      {children}
    </Box>
  );

  const text = (value: string) =>
    value ? (
      <Typography sx={{ fontSize: "0.88rem", color: colors.primary[100], whiteSpace: "pre-wrap" }}>
        {value}
      </Typography>
    ) : null;

  const person = (name: string, picture?: string | null) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Avatar name={name} src={picture ?? undefined} size={36} />
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: colors.primary[100] }}>
        {name}
      </Typography>
    </Box>
  );

  let title = "";
  let subtitle = "";
  let status = "";
  let content: React.ReactNode = null;
  let actions: React.ReactNode = null;

  if (loaded?.queue === "task") {
    const t = loaded.data;
    title = t.title;
    subtitle = t.taskId;
    status = t.status;
    content = (
      <>
        {person(t.internName)}
        {grid(
          <>
            {field("Date", formatDate(t.workDate))}
            {field("Time", `${t.startTime} to ${t.endTime}`)}
            {field("Hours logged", t.hoursLogged)}
            {t.status === "approved" && t.hoursApproved !== null && field("Hours approved", t.hoursApproved)}
            {t.category && field("Category", t.category)}
            {t.project && field("Project", t.project)}
          </>,
        )}
        {text(t.description)}
        {t.rejectionReason && field("Rejection reason", t.rejectionReason)}
      </>
    );
    actions = <TaskPanel task={t} onDone={done} />;
  } else if (loaded?.queue === "report") {
    const r = loaded.data;
    title = r.name;
    subtitle = r.periodLabel;
    status = r.status;
    content = grid(r.metrics.map((m) => <Box key={m.label}>{field(m.label, m.value)}</Box>));
    actions = <ReportPanel report={r} />;
  } else if (loaded) {
    const it = loaded.data;
    title = it.title;
    subtitle = it.kind === "proposal" ? "Proposal" : "Assignment";
    status = it.status;
    content = (
      <>
        {person(it.internName, it.internPicture)}
        {grid(
          <>
            {field(it.kind === "proposal" ? "Estimated reward" : "Reward", money(it.reward))}
            {it.hours !== null && field("Hours", it.hours)}
            {field(it.kind === "proposal" ? "Deadline" : "Due", formatDate(it.dueDate))}
          </>,
        )}
        {text(it.description)}
        {it.feedback && field("Feedback", it.feedback)}
        {it.rejectionReason && field("Rejection reason", it.rejectionReason)}
        <Divider />
        <CommentThread kind={it.kind} itemId={it.id} />
      </>
    );
    actions = <ApprovalPanel item={it} onDone={done} />;
  }

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: colors.primary[900],
          border: `1px solid ${colors.primary[300]}20`,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {loaded ? (
              <>
                <Typography sx={{ fontSize: "0.75rem", color: colors.primary[300] }}>
                  {subtitle}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: colors.primary[100] }}>
                  {title}
                </Typography>
              </>
            ) : (
              <Skeleton width={220} height={28} />
            )}
          </Box>
          {loaded && (
            <Chip
              status={CHIP_STATUS[status] ?? "neutral"}
              label={statusLabel(loaded.queue, status)}
            />
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "8px !important" }}>
        {error && <Alert severity="error">{error}</Alert>}
        {!loaded && !error && <Skeleton variant="rounded" height={180} />}
        {content}
        {loaded && (
          <>
            <Divider />
            {actions}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DetailModal;
